import * as React from 'react';
import { makeStyles, createStyles } from '@bb-ui/react-library';
import { useExperience } from 'contexts/ExperienceContext';

const SESSION_KEY = 'bb-provisioning-ui-banner-dismissed';

const useStyles = makeStyles(() =>
  createStyles({
    banner: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      fontSize: '12.5px',
      fontWeight: 500,
      color: '#ffffff',
      backdropFilter: 'blur(8px)',
      transition: 'background-color 0.3s ease',
      flexShrink: 0,
    },
    bannerModern: {
      backgroundColor: 'rgba(30, 34, 42, 0.95)',
    },
    bannerClassic: {
      backgroundColor: 'rgba(0, 90, 200, 0.92)',
    },
    text: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    separator: {
      opacity: 0.5,
    },
    toggle: {
      textDecoration: 'underline',
      cursor: 'pointer',
      fontWeight: 600,
      color: 'rgba(255, 255, 255, 0.95)',
      background: 'none',
      border: 'none',
      padding: 0,
      fontSize: 'inherit',
      '&:hover': {
        color: '#ffffff',
      },
    },
    closeButton: {
      position: 'absolute',
      right: '12px',
      background: 'none',
      border: 'none',
      color: 'rgba(255, 255, 255, 0.6)',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '4px 8px',
      lineHeight: 1,
      transition: 'color 0.15s ease',
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.9)',
      },
    },
  }),
);

export const ExperienceBanner: React.FC = () => {
  const classes = useStyles();
  const { experience, setExperience, isModern, setBannerVisible } = useExperience();
  const [dismissed, setDismissed] = React.useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Sync banner visibility with context
  React.useEffect(() => {
    setBannerVisible(!dismissed);
  }, [dismissed, setBannerVisible]);

  // Clear dismissed state when experience changes
  React.useEffect(() => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // sessionStorage not available
    }
    setDismissed(false);
  }, [experience]);

  const handleDismiss = React.useCallback(() => {
    setDismissed(true);
    try {
      sessionStorage.setItem(SESSION_KEY, 'true');
    } catch {
      // sessionStorage not available
    }
  }, []);

  const handleToggle = React.useCallback(() => {
    setExperience(isModern ? 'classic' : 'modern');
  }, [isModern, setExperience]);

  if (dismissed) {
    return null;
  }

  return (
    <div className={`${classes.banner} ${isModern ? classes.bannerModern : classes.bannerClassic}`}>
      <span className={classes.text}>
        {isModern ? (
          <>
            You&apos;re using the new experience
            <span className={classes.separator}>·</span>
            <button type="button" className={classes.toggle} onClick={handleToggle}>
              Switch to classic view
            </button>
          </>
        ) : (
          <>
            A new experience is available
            <span className={classes.separator}>·</span>
            <button type="button" className={classes.toggle} onClick={handleToggle}>
              Try it now
            </button>
          </>
        )}
      </span>
      <button
        type="button"
        className={classes.closeButton}
        onClick={handleDismiss}
        aria-label="Dismiss banner"
      >
        ×
      </button>
    </div>
  );
};
